import { api } from "../auth/authApi";
import type { UUID, Paginated, TransferSeries, TransferSeriesWrite, SeriesListParams } from "./types";

const cleanParams = (params: Record<string, any>) => {
  const out: Record<string, any> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    if (typeof v === "string" && v.trim() === "") return;
    out[k] = v;
  });
  return out;
};

export const seriesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    listSeries: builder.query<Paginated<TransferSeries>, SeriesListParams | void>({
      query: (params) => ({
        url: "/transfer-series/",
        method: "GET",
        params: cleanParams(params || {}),
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map((s) => ({ type: "Series" as const, id: s.id })),
              { type: "Series" as const, id: "LIST" },
            ]
          : [{ type: "Series" as const, id: "LIST" }],
    }),

    getSeries: builder.query<TransferSeries, UUID>({
      query: (id) => ({ url: `/transfer-series/${id}/`, method: "GET" }),
      providesTags: (_r, _e, id) => [{ type: "Series", id }],
    }),

    createSeries: builder.mutation<TransferSeries, TransferSeriesWrite>({
      query: (body) => ({ url: "/transfer-series/", method: "POST", body }),
      invalidatesTags: [{ type: "Series", id: "LIST" }],
    }),

    updateSeries: builder.mutation<TransferSeries, { id: UUID; patch: Partial<TransferSeriesWrite> }>({
      query: ({ id, patch }) => ({ url: `/transfer-series/${id}/`, method: "PATCH", body: patch }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "Series", id: arg.id },
        { type: "Series", id: "LIST" },
      ],
    }),

    deleteSeries: builder.mutation<void, { id: UUID }>({
      query: ({ id }) => ({ url: `/transfer-series/${id}/`, method: "DELETE" }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "Series", id: arg.id },
        { type: "Series", id: "LIST" },
      ],
    }),

    generateSeries: builder.mutation<{ created: number }, { id: UUID; generate_until?: string | null }>({
      query: ({ id, generate_until }) => ({
        url: `/transfer-series/${id}/generate/`,
        method: "POST",
        body: generate_until ? { generate_until } : {},
      }),
      invalidatesTags: [{ type: "Transfers", id: "LIST" }],
    }),

    pauseSeries: builder.mutation<{ status: string }, { id: UUID }>({
      query: ({ id }) => ({ url: `/transfer-series/${id}/pause/`, method: "POST" }),
      invalidatesTags: (_r, _e, arg) => [{ type: "Series", id: arg.id }, { type: "Series", id: "LIST" }],
    }),

    resumeSeries: builder.mutation<{ status: string }, { id: UUID }>({
      query: ({ id }) => ({ url: `/transfer-series/${id}/resume/`, method: "POST" }),
      invalidatesTags: (_r, _e, arg) => [{ type: "Series", id: arg.id }, { type: "Series", id: "LIST" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListSeriesQuery,
  useGetSeriesQuery,
  useCreateSeriesMutation,
  useUpdateSeriesMutation,
  useDeleteSeriesMutation,
  useGenerateSeriesMutation,
  usePauseSeriesMutation,
  useResumeSeriesMutation,
} = seriesApi;
